/**
 * Shared data-visualisation primitives.
 *
 * ONE ramp for the whole product. `Funnel` grew its own copy first, and a second blue ramp
 * introduced beside it would mean two charts on the same screen encoding "more" with different
 * blues — the kind of drift nobody notices until the two are compared side by side.
 */

/**
 * Sequential blue ramp, light → dark. Validated with the dataviz validator:
 *
 *   node scripts/validate_palette.js "#86b6ef,#5598e7,#2a78d6,#1c5cab,#104281,#0a2c58" \
 *        --ordinal --mode light --surface "#ffffff"
 *   → ALL CHECKS PASS (monotone L, adjacent ΔL >= 0.06, light end 2.11:1, hue spread 4°)
 *
 * The first five steps are the funnel's ramp unchanged; the sixth extends it so a six-slice
 * donut has a step per slice without re-stepping the ones already in use.
 *
 * LEGAL ONLY ON ORDERED CATEGORIES — funnel stages, journey depth, hour buckets, duration bins.
 * On nominal categories (countries, products) a ramp double-encodes magnitude as hue and burns
 * the only free channel on information the mark's size already carries.
 *
 * The light end sits at 2.11:1 against white, under the 3:1 relief threshold, so every component
 * here ships visible labels and a table twin rather than relying on the fill alone.
 */
export const SEQ_RAMP = ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#104281', '#0a2c58'] as const;

/** Neutral for the folded tail. Never a ramp step — "Other" is not a magnitude. */
export const OTHER_FILL = 'var(--viz-deemphasis)';

/**
 * Ramp step `i` of `n`, spread across the full ramp.
 *
 * Spreading rather than taking the first n keeps the darkest step at the end of every scale, so
 * a 3-slice chart and a 6-slice chart share the same visual endpoints and can be compared.
 */
export function rampStep(i: number, n: number): string {
  if (n <= 1) return SEQ_RAMP[SEQ_RAMP.length - 1]!;
  const pos = Math.round((i / (n - 1)) * (SEQ_RAMP.length - 1));
  return SEQ_RAMP[Math.min(Math.max(pos, 0), SEQ_RAMP.length - 1)]!;
}

/** Cartesian point on a circle. 0° is 12 o'clock, angles run clockwise. */
export function polar(cx: number, cy: number, r: number, degrees: number): [number, number] {
  const rad = ((degrees - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

/**
 * An annular sector — the mark every chart in this file is made of.
 *
 * A full 360° sweep is drawn as two arcs: a single arc command with identical start and end
 * points renders as nothing at all, which is how a "100%" ring silently disappears.
 */
export function arcPath(
  cx: number, cy: number, rInner: number, rOuter: number, from: number, to: number,
): string {
  const sweep = to - from;
  if (sweep <= 0) return '';

  if (sweep >= 359.999) {
    const [ox, oy] = polar(cx, cy, rOuter, 0);
    const [ox2, oy2] = polar(cx, cy, rOuter, 180);
    const [ix, iy] = polar(cx, cy, rInner, 0);
    const [ix2, iy2] = polar(cx, cy, rInner, 180);
    return [
      `M ${ox} ${oy}`,
      `A ${rOuter} ${rOuter} 0 0 1 ${ox2} ${oy2}`,
      `A ${rOuter} ${rOuter} 0 0 1 ${ox} ${oy}`,
      `M ${ix} ${iy}`,
      `A ${rInner} ${rInner} 0 0 0 ${ix2} ${iy2}`,
      `A ${rInner} ${rInner} 0 0 0 ${ix} ${iy}`,
      'Z',
    ].join(' ');
  }

  const large = sweep > 180 ? 1 : 0;
  const [x1, y1] = polar(cx, cy, rOuter, from);
  const [x2, y2] = polar(cx, cy, rOuter, to);
  const [x3, y3] = polar(cx, cy, rInner, to);
  const [x4, y4] = polar(cx, cy, rInner, from);

  return [
    `M ${x1} ${y1}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${x4} ${y4}`,
    'Z',
  ].join(' ');
}

/** `0.0731` → `7.3%`. Dashes on a null so an unmeasured ratio never reads as zero. */
export function formatPercent(share: number | null | undefined, digits = 1): string {
  if (share === null || share === undefined || !Number.isFinite(share)) return '—';
  return `${(share * 100).toFixed(digits)}%`;
}

/**
 * Milliseconds as a duration a person reads at a glance: `4.2s`, `1m 12s`, `2h 05m`.
 *
 * Never bare milliseconds. "612000" and "10m 12s" are the same number, and only one of them
 * gets checked against intuition.
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;

  const totalSeconds = ms / 1000;
  if (totalSeconds < 60) return `${totalSeconds.toFixed(1)}s`;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds % 60);
  if (minutes < 60) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;

  const hours = Math.floor(minutes / 60);
  return `${hours}h ${String(minutes % 60).padStart(2, '0')}m`;
}
